import { useState } from "react";
import { useAction } from "convex/react";
import { useLocation } from "wouter";
import { X, Lightbulb } from "lucide-react";
import { api } from "convex/_generated/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { DanceStyleSelect } from "./DanceStyleSelect";
import { useCurrentUser } from "../../hooks/useCurrentUser";

interface VenueSuggestModalProps {
  onClose: () => void;
  onCreated?: (venueId: string) => void;
}

export function VenueSuggestModal({ onClose, onCreated }: VenueSuggestModalProps) {
  const { isLoggedIn } = useCurrentUser();
  const [, navigate] = useLocation();
  const createVenue = useAction(api.venues.createVenueWithGeocode);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");
  const [danceStyles, setDanceStyles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  if (!isLoggedIn) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-background rounded-xl border border-border w-full max-w-sm p-8 text-center shadow-xl">
          <Lightbulb className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="font-semibold text-lg text-foreground mb-2">Sign in to suggest a venue</h2>
          <p className="text-sm text-muted-foreground mb-6">
            You need an account to suggest venues to the directory.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => { onClose(); navigate("/login"); }}>Sign In</Button>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-background rounded-xl border border-border w-full max-w-sm p-8 text-center shadow-xl">
          <Lightbulb className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="font-semibold text-lg text-foreground mb-2">Thanks for the suggestion!</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Your venue has been added to the directory. A studio owner can claim it as their own.
          </p>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Venue name is required."); return; }
    if (!address.trim()) { setError("Address is required."); return; }

    setSaving(true);
    setError(null);
    try {
      const venueId = await createVenue({
        name: name.trim(),
        address: address.trim(),
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        website: website.trim() || undefined,
        description: description.trim() || undefined,
        danceStyles: danceStyles.length > 0 ? danceStyles : undefined,
      });
      onCreated?.(venueId);
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message ?? "Failed to suggest venue.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-background rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-background z-10 border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h2 className="font-semibold text-lg text-foreground flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                Suggest a Venue
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Know a place where dances happen? Add it to the community directory.
              </p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          {error && (
            <div className="px-6 pb-3">
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Venue Name <span className="text-destructive">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. The Grand Ballroom"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Address <span className="text-destructive">*</span>
            </label>
            <AddressAutocomplete
              value={address}
              onChange={(val) => { setAddress(val); setCoords(null); }}
              onPlaceSelect={(place) => {
                setAddress(place.address);
                setCoords(place.coords);
              }}
              placeholder="Start typing the address..."
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Website</label>
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://..."
              type="url"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell people about this venue..."
              rows={3}
            />
          </div>

          {/* Dance Styles */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Dance Styles</label>
            <DanceStyleSelect value={danceStyles} onChange={setDanceStyles} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Submitting..." : "Submit Suggestion"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
